import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types/database';

/**
 * Supabase-Client als Factory + Registry statt Modul-Singleton (Phase 0.6).
 *
 * Der alte Singleton (src/lib/supabase.ts) las import.meta.env beim
 * Import — Browser-/Vite-Semantik, die es in React Native/Metro nicht
 * gibt. Jetzt baut die Plattform (Web-Boot bzw. später App-Boot) den
 * Client mit ihrer Konfiguration und registriert ihn; geteilte Logik
 * holt ihn über getSupabase(), ohne die Umgebung zu kennen.
 *
 * Generic <Db>: DIESER GRUND IST ENTFALLEN. Die generierten DB-Typen lagen
 * bis 0.12 unter src/types, und core darf nicht aus src importieren
 * (guard:core) — deshalb war die Factory typoffen. Seit database.ts unter
 * packages/core/src/types/ liegt, kann core sie selbst ziehen. Der Default
 * ist jetzt <Database>: wer getSupabase() aufruft, bekommt einen geprüften
 * Client, ohne etwas dafür zu tun.
 *
 * ⚠️ Der Database-Import steht als `import type` auf STATEMENT-Ebene und
 * relativ OHNE `.js`. Beides ist Pflicht, beides aus einem Ausfall gelernt:
 * `import { type Database }` emittiert unter verbatimModuleSyntax einen
 * echten Laufzeit-Import (20.08., acht Endpunkte tot), und eine `.js`-Endung
 * an einem relativen core-Import bricht Metro (10.08.). In api/ gilt die
 * umgekehrte Regel — die Endung ist dort Pflicht.
 */

/** Storage-Vertrag von supabase-js (sync im Browser, async in RN). */
export type SupabaseAuthStorage = {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
};

export type SupabaseAppConfig = {
  url: string;
  anonKey: string;
  /** X-Client-Info-Header, z. B. 'die-sizilianer-web' / 'die-sizilianer-pass'. */
  clientInfo: string;
  auth: {
    /** Web: weglassen (localStorage-Default). RN: AsyncStorage-Adapter. */
    storage?: SupabaseAuthStorage;
    /** Web: true (Magic-/Recovery-Links). RN: false — es gibt keine URL. */
    detectSessionInUrl: boolean;
    flowType: 'pkce' | 'implicit';
  };
};

export function createSupabaseAppClient<Db = unknown>(
  cfg: SupabaseAppConfig,
): SupabaseClient<Db> {
  // Intern untypisiert: die konditionalen Schema-Generics von supabase-js
  // lassen sich mit einem offenen Db-Parameter nicht durchreichen — das
  // Generic dient allein dem Aufrufer, der sein Schema kennt.
  const client = createClient(cfg.url, cfg.anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: cfg.auth.detectSessionInUrl,
      flowType: cfg.auth.flowType,
      ...(cfg.auth.storage ? { storage: cfg.auth.storage } : {}),
    },
    db: { schema: 'public' },
    global: { headers: { 'X-Client-Info': cfg.clientInfo } },
  });
  return client as unknown as SupabaseClient<Db>;
}

// Die Registry kennt das Schema jetzt: es gibt genau eine Datenbank, und
// beide Boots (Web und apps/pass) reichen einen typisierten Client herein.
let registered: SupabaseClient<Database> | null = null;

export function registerSupabase(client: SupabaseClient<Database>): void {
  registered = client;
}

/**
 * Wirft bewusst, wenn der Boot die Registrierung vergessen hat — ein
 * still fehlender Client wäre der schwerere, spätere Fehler.
 *
 * OHNE TYPPARAMETER: ein Typargument an dieser Funktion ist ab hier ein
 * Übersetzungsfehler (TS2558 „Expected 0 type arguments, but got 1") und kein
 * Wächterthema mehr. Der Prüfer selbst verhindert, dass jemand den Typ wieder
 * wegwirft — das ist die stärkere Sicherung, weil sie nicht vergessen werden
 * kann.
 */
export function getSupabase(): SupabaseClient<Database> {
  if (!registered) {
    throw new Error(
      'supabaseFactory: kein Client registriert — Boot-Reihenfolge prüfen (registerSupabase fehlt).',
    );
  }
  return registered;
}
